// client/src/pages/SherlocAuthorizations.js
// CLIENT – Sherloc Authorizations (matches Superadmin design + D-Days pills)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import "./SherlocMediaCampaigns.css";
import "./SherlocAuthorizations.css";
import "./MediaAuthorizationsResponsive.css";
import "./MediaAuthorizationsPerimeter.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import authorizeHero from "../assets/authorize.jpg";

import { useNavigate } from "react-router-dom";
import { formatFlightDate, formatFlightRange } from "../utils/flightDates";
import { reportingDaysUntil } from "../utils/reportingCalendar";
import {
  isFullyExecutedAuthorization,
  resolveMediaAuthorizationStatus,
} from "../utils/mediaAuthorizationStatus";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const ESIGN_BASE_PATH = "/api/esign";
const CAMPAIGNS_PATH_CANDIDATES = [
  "/api/media-campaigns",
  "/api/mediacampaigns",
  "/api/campaigns",
  "/api/sherloc-media-campaigns",
  "/api/sherlocMediaCampaigns",
];

const safeBase = (base) => String(base || "").replace(/\/+$/, "");

const getStoredToken = () =>
  localStorage.getItem("authToken") ||
  localStorage.getItem("token") ||
  localStorage.getItem("jwt") ||
  "";

// ---- Internal-row visibility (defensive; server-side filter is authoritative) ----
// The backend already excludes internal review/test/reporting-only rows for
// normal client users; this mirrors that rule so a stale cache or older API
// can never render internal rows to an unauthorized viewer.
const INTERNAL_AUTHORIZATION_VIEWER_EMAILS = ["david.johnson21@gmail.com"];

const getStoredViewer = () => ({
  email: String(localStorage.getItem("email") || localStorage.getItem("userEmail") || "")
    .trim()
    .toLowerCase(),
  role: String(localStorage.getItem("role") || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]/g, ""),
});

const canViewInternalAuthorizations = (viewer) =>
  viewer?.role === "superadmin" ||
  (Boolean(viewer?.email) && INTERNAL_AUTHORIZATION_VIEWER_EMAILS.includes(viewer.email));

const INTERNAL_ROW_NAME_MARKERS = [
  /ads[\s_-]?read/i,
  /\bverification\b/i,
  /\bsandbox\b/i,
  /\brehearsal\b/i,
  /\binternal[\s_-]+(?:test|review|only)\b/i,
];

const isInternalAuthorizationRow = (c) => {
  const status = String(c?.raw_status ?? c?.status ?? "").trim().toUpperCase();
  if (status === "REPORTING_ONLY") return true;

  const payload = c?.strategy_payload ?? c?.strategyPayload ?? null;
  if (payload && typeof payload === "object") {
    if (payload.reportingOnly === true) return true;
    if (payload.internalOnly === true) return true;
    const external = payload.externalReporting;
    if (external && typeof external === "object") {
      if (external.readOnly === true) return true;
      if (external.createdForReview === true) return true;
      if (external.launchable === false) return true;
      if (external.deploymentEligible === false) return true;
    }
  }

  const name = String(c?.campaign_name ?? c?.campaignName ?? c?.name ?? "");
  if (INTERNAL_ROW_NAME_MARKERS.some((re) => re.test(name))) return true;

  if (status === "DRAFT" && /\b(?:test|review)\b/i.test(name)) return true;

  return false;
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.campaigns)) return data.campaigns;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.mediaCampaigns)) return data.mediaCampaigns;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.advertisers)) return data.advertisers;
  return [];
};

const parseDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date && !isNaN(value)) return value;

  if (typeof value === "string") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const d = Number(m[3]);
      const dt = new Date(y, mo, d);
      return isNaN(dt) ? null : dt;
    }
  }

  const dt = new Date(value);
  return isNaN(dt) ? null : dt;
};

const formatMoneySafe = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString()}`;
};


const hoursDiff = (from, to) => {
  const a = from.getTime();
  const b = to.getTime();
  return Math.floor((b - a) / 3600000);
};

// ---- Campaign field getters (robust to shape differences) ----
const getCampaignId = (c) => c?.campaign_id ?? c?.campaignId ?? c?.id ?? null;

const getCampaignName = (c) => c?.campaign_name ?? c?.campaignName ?? c?.name ?? c?.title ?? "—";

const getAdvertiserName = (c) =>
  c?.advertiser_name ?? c?.advertiserName ?? c?.Advertiser?.name ?? "—";

const getFlightStart = (c) =>
  c?.flight_start_date ?? c?.flightStartDate ?? c?.start_date ?? c?.startDate ?? null;

const getFlightEnd = (c) =>
  c?.flight_end_date ?? c?.flightEndDate ?? c?.end_date ?? c?.endDate ?? null;

const getBudget = (c) => c?.total_budget ?? c?.totalBudget ?? c?.budget ?? c?.totalBudgetUsd ?? null;

// Contract nested payload merge (same behavior as superadmin)
const getStrategyContract = (campaign) => {
  const spc = campaign?.strategy_payload_contract ?? campaign?.strategyPayloadContract ?? null;
  if (spc) {
    if (typeof spc === "string") {
      try {
        return JSON.parse(spc) || null;
      } catch {
        return null;
      }
    }
    if (typeof spc === "object") return spc;
  }

  const sp = campaign?.strategy_payload ?? campaign?.strategyPayload ?? null;
  if (!sp) return null;

  if (typeof sp === "string") {
    try {
      const parsed = JSON.parse(sp);
      return parsed?.contract || null;
    } catch {
      return null;
    }
  }

  return sp?.contract || null;
};

const getContractSource = (campaign) => {
  const nested = getStrategyContract(campaign) || {};
  const flat = campaign && typeof campaign === "object" ? campaign : {};
  return { ...nested, ...flat };
};

const getContractStatus = (c) => {
  const src = getContractSource(c);

  const raw =
    src.contract_status ??
    src.contractStatus ??
    src.authorization_status ??
    src.authorizationStatus ??
    src.status ??
    src.approval_status ??
    "NOT_STARTED";

  const normalized = String(raw).toUpperCase();
  return normalized === "NOT_STARTED" ? "PENDING" : normalized;
};

const getEnvelopeId = (c) => {
  const src = getContractSource(c);
  return (
    src.envelope_id ??
    src.envelopeId ??
    src.docusign_envelope_id ??
    src.docusignEnvelopeId ??
    null
  );
};

const getClientSignedAt = (c) => {
  const src = getContractSource(c);
  return src.client_signed_at ?? src.clientSignedAt ?? src.signed_at ?? src.signedAt ?? null;
};

const getPdfUrl = (c) => {
  const src = getContractSource(c);
  return (
    src.contract_pdf_url ??
    src.pdf_url ??
    src.pdfUrl ??
    src.s3_pdf_url ??
    src.s3PdfUrl ??
    src.authorization_pdf_url ??
    src.authorizationPdfUrl ??
    null
  );
};

const getSigningUrl = (c) => {
  const src = getContractSource(c);
  return (
    src.signing_url ??
    src.signingUrl ??
    src.docusign_signing_url ??
    src.docusignSigningUrl ??
    src.authorization_signing_url ??
    src.authorizationSigningUrl ??
    null
  );
};

const getExplicitBoolean = (source, keys) => {
  for (const key of keys) {
    if (typeof source?.[key] === "boolean") return source[key];
  }
  return null;
};

const getContractDownloadSource = (c) => {
  const src = getContractSource(c);
  return String(
    src.contract_download_source ?? src.contractDownloadSource ?? ""
  )
    .trim()
    .toLowerCase();
};

const isDocusignHost = (host) => {
  const normalized = String(host || "").trim().toLowerCase();
  return (
    normalized === "docusign.com" ||
    normalized.endsWith(".docusign.com") ||
    normalized === "docusign.net" ||
    normalized.endsWith(".docusign.net")
  );
};

const isCphereHost = (host) => {
  const normalized = String(host || "").trim().toLowerCase();
  return normalized === "cphere.ai" || normalized.endsWith(".cphere.ai");
};

const isSafeContractPath = (pathname) => {
  const normalized = String(pathname || "").trim().toLowerCase();
  return (
    normalized.endsWith(".pdf") ||
    normalized.startsWith(`${ESIGN_BASE_PATH}/campaigns/`) ||
    normalized.startsWith("/contracts/") ||
    normalized.startsWith("/authorizations/contracts/") ||
    normalized.startsWith("/uploads/contracts/")
  );
};

const isGovernedContractUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;

  try {
    const url = new URL(toAbsoluteUrl(raw));
    if (url.protocol !== "https:" || url.username || url.password) {
      return false;
    }
    if (isDocusignHost(url.hostname)) return true;

    const frontendOrigin = new URL(window.location.origin).origin;
    const backendOrigin = new URL(
      safeBase(API_BASE_URL) || window.location.origin
    ).origin;
    return (
      (url.origin === frontendOrigin ||
        url.origin === backendOrigin ||
        isCphereHost(url.hostname)) &&
      isSafeContractPath(url.pathname)
    );
  } catch {
    return false;
  }
};

const isContractDownloadAvailable = (c) => {
  const src = getContractSource(c);
  const explicit = getExplicitBoolean(src, [
    "contract_download_available",
    "contractDownloadAvailable",
  ]);

  if (explicit === false) return false;

  const governedPdfAvailable = isGovernedContractUrl(getPdfUrl(c));
  const envelopeAvailable = Boolean(getEnvelopeId(c));
  if (explicit === true) {
    const source = getContractDownloadSource(c);
    if (source === "external_pdf") return governedPdfAvailable;
    if (source === "docusign_envelope") return envelopeAvailable;
    return Boolean(governedPdfAvailable || envelopeAvailable);
  }

  // Backward compatibility for an older API response that predates the
  // explicit capability. Client can retrieve only an external PDF or the
  // protected DocuSign document related through the campaign envelope.
  return Boolean(governedPdfAvailable || envelopeAvailable);
};

const isGovernedSigningUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;

  try {
    const url = new URL(toAbsoluteUrl(raw));
    if (url.username || url.password) return false;
    const host = url.hostname.toLowerCase();
    const governedApiOrigin = new URL(
      safeBase(API_BASE_URL) || window.location.origin
    ).origin;
    return (
      url.protocol === "https:" &&
      (isDocusignHost(host) ||
        (url.origin === governedApiOrigin &&
          url.pathname.startsWith(`${ESIGN_BASE_PATH}/signing-links/`)))
    );
  } catch {
    return false;
  }
};

const isGovernedShareableSigningUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return false;

  try {
    const url = new URL(toAbsoluteUrl(raw));
    const governedApiOrigin = new URL(
      safeBase(API_BASE_URL) || window.location.origin
    ).origin;
    return Boolean(
      url.protocol === "https:" &&
        !url.username &&
        !url.password &&
        url.origin === governedApiOrigin &&
        url.pathname.startsWith(`${ESIGN_BASE_PATH}/signing-links/`)
    );
  } catch {
    return false;
  }
};

const isSigningLinkAvailable = (c) => {
  const src = getContractSource(c);
  const explicit = getExplicitBoolean(src, [
    "signer_link_available",
    "signerLinkAvailable",
  ]);

  if (explicit === false) return false;
  const hasGovernedReference = Boolean(
    isGovernedShareableSigningUrl(getSigningUrl(c)) || getEnvelopeId(c)
  );
  if (explicit === true) return hasGovernedReference;
  return hasGovernedReference;
};

const getSignerEmail = (c) => {
  const src = getContractSource(c);
  return src.signer_email ?? src.signerEmail ?? null;
};

const getSignerName = (c) => {
  const src = getContractSource(c);
  return src.signer_name ?? src.signerName ?? null;
};

// ✅ Primary user-facing Authorization status.
// The table row, the authorization-details popup, and the pending filter all read
// it from the shared canonical resolver, so a record can never disagree with
// itself. getContractStatus() stays the RAW DocuSign/contract state and is still
// what drives urgency and diagnostics.
const getGovernedStatus = (c) => resolveMediaAuthorizationStatus(c).status;

const COMPLETED_CONTRACT_STATUSES = new Set(["COMPLETED", "EXECUTED", "COUNTERSIGNED"]);
const PROBLEM_CONTRACT_STATUSES = new Set(["VOIDED", "DECLINED", "EXPIRED"]);
const PENDING_CONTRACT_STATUSES = new Set([
  "PENDING",
  "CREATED",
  "IN_PROCESS",
  "PROCESSING",
  "SENT",
  "AWAITING_CLIENT",
  "DELIVERED",
  "UNDER_REVIEW",
  "UNDER-REVIEW",
  "AWAITING_COUNTERSIGN",
]);

const prettyStatus = (s) => String(s || "PENDING").replaceAll("_", " ");
const normalizeAuthorizationStatusVisualKey = (s) =>
  String(s || "neutral")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "") || "neutral";
const AUTHORIZATION_STATUS_VISUAL_KEYS = new Set([
  "authorized",
  "approved",
  "under_review",
  "draft",
  "reporting_only",
  "needs_reapproval",
  "pending",
  "not_started",
  "created",
  "in_process",
  "processing",
  "sent",
  "awaiting_client",
  "delivered",
  "client_signed",
  "awaiting_countersign",
  "ready_to_resend",
  "completed",
  "executed",
  "countersigned",
  "voided",
  "declined",
  "expired",
  "refused",
  "rejected",
  "blocked",
  "invalid",
  "superseded",
  "authorization_unavailable",
  "canceled",
  "cancelled",
]);
const getAuthorizationStatusVisualKey = (status) => {
  const normalized = normalizeAuthorizationStatusVisualKey(status);
  return AUTHORIZATION_STATUS_VISUAL_KEYS.has(normalized) ? normalized : "neutral";
};
const urgencyToken = (u) =>
  String(u || "LOW")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
const prettyUrgency = (u) => String(u || "LOW").replaceAll("_", " ");

const getRawUrgency = (c) => {
  const raw =
    c?.urgency ??
    c?.Urgency ??
    c?.priority ??
    c?.Priority ??
    c?.review_urgency ??
    c?.reviewUrgency ??
    c?.risk_level ??
    c?.riskLevel ??
    "LOW";
  return String(raw).toUpperCase();
};

const computeUrgencyFromStartDate = (startDt, now) => {
  if (!startDt || !(startDt instanceof Date) || isNaN(startDt)) return null;
  const daysUntilStart = reportingDaysUntil(startDt, now);
  if (daysUntilStart >= 30) return "LOW";
  if (daysUntilStart >= 15) return "MEDIUM";
  if (daysUntilStart >= 6) return "HIGH";
  return "CRITICAL";
};

const getCampaignOperationalState = (campaign, now) => {
  const statusRaw = getContractStatus(campaign);
  const status = String(statusRaw || "").toUpperCase();
  const startDt = parseDateSafe(getFlightStart(campaign));
  const endDt = parseDateSafe(getFlightEnd(campaign));

  const isCompleted = COMPLETED_CONTRACT_STATUSES.has(status);
  const isProblem = PROBLEM_CONTRACT_STATUSES.has(status);
  const isPending = PENDING_CONTRACT_STATUSES.has(status);

  const hasStarted = startDt ? reportingDaysUntil(startDt, now) <= 0 : false;
  const hasEnded = endDt ? reportingDaysUntil(endDt, now) < 0 : false;
  const isUpcoming = startDt ? reportingDaysUntil(startDt, now) > 0 : false;
  const isLive = hasStarted && !hasEnded;

  return {
    status,
    startDt,
    endDt,
    isCompleted,
    isProblem,
    isPending,
    isUpcoming,
    isLive,
    hasEnded,
    isHistoricalCompleted: isCompleted && hasEnded,
  };
};

const getUrgency = (campaign, now = new Date()) => {
  const operational = getCampaignOperationalState(campaign, now);
  const fallbackUrgency = getRawUrgency(campaign);

  if (operational.isCompleted) {
    return "COMPLETED";
  }

  if (operational.isProblem) {
    if (operational.isLive) return "CRITICAL";
    if (operational.isUpcoming) return "HIGH";
    return "HIGH";
  }

  if (operational.status === "AWAITING_COUNTERSIGN") {
    if (operational.isLive) return "CRITICAL";
    if (operational.isUpcoming) {
      return computeUrgencyFromStartDate(operational.startDt, now) || "HIGH";
    }
    return operational.hasEnded ? "MEDIUM" : "HIGH";
  }

  if (operational.hasEnded) {
    if (operational.isPending) return "MEDIUM";
    return "LOW";
  }

  const computedUrgency = computeUrgencyFromStartDate(operational.startDt, now);
  return computedUrgency || fallbackUrgency;
};

const computeDDaysDisplay = (campaign, now) => {
  const statusRaw = getContractStatus(campaign);
  const clientSignedAt = parseDateSafe(getClientSignedAt(campaign));
  const operational = getCampaignOperationalState(campaign, now);
  const startDt = operational.startDt;
  const endDt = operational.endDt;

  let countdownDisplay = "—";
  let countdownTitle = "";

  if (String(statusRaw).toUpperCase() === "AWAITING_COUNTERSIGN" && clientSignedAt) {
    const h = hoursDiff(clientSignedAt, now);
    countdownDisplay = h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
    countdownTitle = `Awaiting countersignature for ${h} hours`;
    return { countdownDisplay, countdownTitle };
  }

  if (!startDt) return { countdownDisplay, countdownTitle };

  const calUntilStart = reportingDaysUntil(startDt, now);
  if (operational.isUpcoming && calUntilStart > 0) {
    countdownDisplay = String(calUntilStart);
    countdownTitle = `${calUntilStart} days until flight start (ET)`;
    return { countdownDisplay, countdownTitle };
  }

  if (calUntilStart === 0) {
    countdownDisplay = "D-DAY";
    countdownTitle = "Flight starts today (ET)";
    return { countdownDisplay, countdownTitle };
  }

  if (operational.hasEnded) {
    if (endDt) {
      const calUntilEnd = reportingDaysUntil(endDt, now);
      countdownDisplay = "ENDED";
      countdownTitle = `Ended ${Math.abs(calUntilEnd)} days ago (ET)`;
      return { countdownDisplay, countdownTitle };
    }
    countdownDisplay = "ENDED";
    countdownTitle = "Flight has ended";
    return { countdownDisplay, countdownTitle };
  }

  if (operational.isLive && endDt) {
    const calUntilEnd = reportingDaysUntil(endDt, now);
    countdownDisplay = "LIVE";
    countdownTitle = `Live • ${calUntilEnd} days until end (ET)`;
  } else if (operational.isLive) {
    countdownDisplay = "LIVE";
    countdownTitle = "Flight in progress";
  }

  return { countdownDisplay, countdownTitle };
};

const getDDaysLifecycleState = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized === "LIVE" || normalized === "ENDED" ? normalized.toLowerCase() : null;
};

async function apiBlob({ baseUrl, path, token }) {
  const url = `${safeBase(baseUrl)}${path}${path.includes("?") ? "&" : "?"}ts=${Date.now()}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: "include",
    cache: "no-store",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    return { ok: false, status: res.status, blob: null, text };
  }

  const blob = await res.blob();
  return { ok: true, status: res.status, blob, text: "" };
}

async function apiJson({ baseUrl, path, method = "GET", token, body }) {
  const url = `${safeBase(baseUrl)}${path}${path.includes("?") ? "&" : "?"}ts=${Date.now()}`;

  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    cache: "no-store",
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text().catch(() => "");
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  return { ok: res.ok, status: res.status, data, text };
}

const extractEsignMessage = (resp) => {
  const d = resp?.data || null;
  const primary = d?.message || d?.error || d?.title || d?.detail || "";
  const hint =
    d?.hint || d?.details?.message || (typeof d?.details === "string" ? d.details : "") || "";
  const extra = resp?.text && !d ? resp.text : "";

  return {
    msg: String(primary || extra || "").trim(),
    hint: String(hint || "").trim(),
  };
};

const getStructuredErrorCode = (resp) => {
  if (resp?.data && typeof resp.data === "object") {
    return String(resp.data.code || "").trim().toUpperCase();
  }

  try {
    const parsed = resp?.text ? JSON.parse(resp.text) : null;
    return String(parsed?.code || "").trim().toUpperCase();
  } catch {
    return "";
  }
};

const withTs = (url) => {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("ts", String(Date.now()));
    return u.toString();
  } catch {
    const sep = String(url).includes("?") ? "&" : "?";
    return `${url}${sep}ts=${Date.now()}`;
  }
};

const isApiLikeUrl = (url) => {
  const s = String(url || "").trim();
  if (!s) return false;
  if (s.startsWith("/api/")) return true;
  try {
    const u = new URL(s, window.location.origin);
    return u.pathname.startsWith("/api/");
  } catch {
    return false;
  }
};

const toAbsoluteUrl = (maybeUrl) => {
  const raw = String(maybeUrl || "").trim();
  if (!raw) return "";
  try {
    return new URL(raw).toString();
  } catch {
    const base = safeBase(API_BASE_URL);
    if (!base) return raw;
    if (raw.startsWith("//")) return `https:${raw}`;
    return `${base}${raw.startsWith("/") ? "" : "/"}${raw}`;
  }
};

const MODAL_FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const SherlocAuthorizations = () => {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [pendingOnly, setPendingOnly] = useState(true);
  const [now, setNow] = useState(() => new Date());

  // Modal
  const [open, setOpen] = useState(false);
  // The snapshot captured when the row was opened. Rendering never reads it
  // directly — see the `active` projection below.
  const [activeSnapshot, setActive] = useState(null);

  // ✅ Single canonical source for the authorization-details popup.
  // Whenever the opened record is still in `campaigns`, the popup renders that
  // live row, so the background refresh can never leave the table and the popup
  // showing different authorization states. The captured snapshot is only a
  // fallback for a record that has left the list.
  const active = useMemo(() => {
    if (!activeSnapshot) return null;
    const activeId = getCampaignId(activeSnapshot);
    if (!activeId) return activeSnapshot;
    return (
      campaigns.find((c) => String(getCampaignId(c)) === String(activeId)) || activeSnapshot
    );
  }, [campaigns, activeSnapshot]);

  const [heroFailed, setHeroFailed] = useState(false);

  const closeBtnRef = useRef(null);
  const modalRef = useRef(null);
  const modalReturnFocusRef = useRef(null);

  const toast = (msg) => {
    setNotice(String(msg || ""));
    setTimeout(() => setNotice(""), 1600);
  };

  const handleLogoClick = () => navigate("/dashboard");

  const fetchCampaigns = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");

    try {
      const token = getStoredToken();

      if (!API_BASE_URL) {
        setError("Backend URL is not configured. Check REACT_APP_BACKEND_URL.");
        setCampaigns([]);
        return [];
      }

      let lastErr = "";
      for (const path of CAMPAIGNS_PATH_CANDIDATES) {
        const url = `${safeBase(API_BASE_URL)}${path}?ts=${Date.now()}`;

        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          cache: "no-store",
          credentials: "include",
        });

        if (!res.ok) {
          lastErr = `(${res.status}) ${path}`;
          continue;
        }

        const data = await res.json().catch(() => ({}));
        const list = normalizeList(data);
        const next = Array.isArray(list) ? list : [];
        setCampaigns(next);
        return next;
      }

      setCampaigns([]);
      setError(
        `Unable to load authorizations. None of these routes responded: ${CAMPAIGNS_PATH_CANDIDATES.join(
          ", "
        )}. Last error: ${lastErr || "unknown"}`
      );
      return [];
    } catch (err) {
      console.error("[Client SherlocAuthorizations] Network error:", err);
      setError("Network error loading campaigns. Please try again.");
      setCampaigns([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => fetchCampaigns({ silent: true }), 5 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const viewer = getStoredViewer();
    const showInternal = canViewInternalAuthorizations(viewer);

    return campaigns.filter((c) => {
      if (!showInternal && isInternalAuthorizationRow(c)) return false;

      if (!pendingOnly) return true;
      // client view: hide fully executed authorizations. AUTHORIZED records are
      // still awaiting countersignature, so they stay in the pending queue.
      return !isFullyExecutedAuthorization(c);
    });
  }, [campaigns, pendingOnly]);

  const openModal = (row, returnFocusTarget = null) => {
    modalReturnFocusRef.current =
      returnFocusTarget && typeof returnFocusTarget.focus === "function"
        ? returnFocusTarget
        : document.activeElement;
    setActive(row);
    setHeroFailed(false);
    setOpen(true);
  };

  const closeModal = useCallback(() => {
    const returnFocusTarget = modalReturnFocusRef.current;
    modalReturnFocusRef.current = null;
    setOpen(false);
    setActive(null);
    setTimeout(() => returnFocusTarget?.focus?.(), 0);
  }, []);

  useEffect(() => {
    if (!open || !active) return undefined;

    closeBtnRef.current?.focus?.();

    const handleModalKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        modalRef.current?.querySelectorAll?.(MODAL_FOCUSABLE_SELECTOR) || []
      );
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleModalKeyDown);
    return () => document.removeEventListener("keydown", handleModalKeyDown);
  }, [active, closeModal, open]);

  const handleDownload = async (row) => {
    const token = getStoredToken();
    setError("");

    if (!isContractDownloadAvailable(row)) return;

    const pdfUrlRaw = getPdfUrl(row);
    const pdfUrl = pdfUrlRaw ? toAbsoluteUrl(pdfUrlRaw) : "";

    // If it's an external PDF URL, open directly
    if (pdfUrl && isGovernedContractUrl(pdfUrl) && !isApiLikeUrl(pdfUrl)) {
      window.open(withTs(pdfUrl), "_blank", "noopener,noreferrer");
      return;
    }

    // Otherwise, try protected API download
    const campaignId = getCampaignId(row);
    if (!API_BASE_URL || !token || !campaignId) {
      setError("PDF not available yet.");
      return;
    }

    const candidates = [
      `${ESIGN_BASE_PATH}/campaigns/${encodeURIComponent(String(campaignId))}/download?portal=client`,
      `${ESIGN_BASE_PATH}/campaigns/${encodeURIComponent(String(campaignId))}/download-pdf?portal=client`,
      `${ESIGN_BASE_PATH}/campaigns/${encodeURIComponent(String(campaignId))}/pdf?portal=client`,
    ];

    let last = null;
    for (const path of candidates) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const resp = await apiBlob({ baseUrl: API_BASE_URL, path, token });
        last = resp;
        if (!resp.ok || !resp.blob) continue;

        const blobUrl = URL.createObjectURL(resp.blob);
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        return;
      } catch {
        last = { ok: false, status: 0, blob: null, text: "" };
      }
    }

    const code = getStructuredErrorCode(last);
    console.warn("[Client SherlocAuthorizations] Contract retrieval failed.", {
      status: last?.status || 0,
      code: code || "CONTRACT_RETRIEVAL_FAILED",
    });
    setError(
      code === "CONTRACT_NOT_AVAILABLE"
        ? "NO DOWNLOADABLE CONTRACT IS AVAILABLE FOR THIS CAMPAIGN."
        : "CONTRACT COULD NOT BE RETRIEVED."
    );
  };

  const handleOpenSigningLink = async (row) => {
    setError("");

    if (!isSigningLinkAvailable(row)) return;

    const storedSigningUrl = getSigningUrl(row);
    if (isGovernedShareableSigningUrl(storedSigningUrl)) {
      window.open(withTs(toAbsoluteUrl(storedSigningUrl)), "_blank", "noopener,noreferrer");
      return;
    }

    const campaignId = getCampaignId(row);
    const token = getStoredToken();
    if (!campaignId) {
      setError("No campaign id found for this authorization.");
      return;
    }
    if (!getEnvelopeId(row)) {
      console.warn("[Client SherlocAuthorizations] Signing capability lacks a governed reference.");
      setError("SIGNING LINK COULD NOT BE OPENED.");
      return;
    }
    if (!API_BASE_URL || !token) {
      setError("Signing is not available right now.");
      return;
    }

    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) {
      setError("Pop-up was blocked. Please allow pop-ups for this site and try again.");
      return;
    }

    try {
      w.document.write("<title>Opening signing link…</title><body style=\"background:#000;color:#fff;font-family:sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;\">Opening signing link…</body>");
      w.document.close();
    } catch {}

    try {
      const resp = await apiJson({
        baseUrl: API_BASE_URL,
        path: `${ESIGN_BASE_PATH}/campaigns/${encodeURIComponent(String(campaignId))}/signing-link?portal=client`,
        method: "POST",
        token,
        body: {
          signerEmail: getSignerEmail(row) || undefined,
          signerName: getSignerName(row) || undefined,
          portal: "client",
        },
      });

      if (!resp.ok) {
        const { msg, hint } = extractEsignMessage(resp);
        const code = getStructuredErrorCode(resp);
        try {
          w.close();
        } catch {}
        console.warn("[Client SherlocAuthorizations] Signing-link retrieval failed.", {
          status: resp.status || 0,
          code: code || "SIGNING_LINK_RETRIEVAL_FAILED",
          hasSafeMessage: Boolean(msg || hint),
        });
        setError("SIGNING LINK COULD NOT BE OPENED.");
        return;
      }

      const rawUrl = resp?.data?.signingUrl || resp?.data?.url || "";
      const url = toAbsoluteUrl(rawUrl);
      if (!isGovernedSigningUrl(url)) {
        try {
          w.close();
        } catch {}
        console.warn("[Client SherlocAuthorizations] Signing service returned an invalid destination.");
        setError("SIGNING LINK COULD NOT BE OPENED.");
        return;
      }

      w.location.replace(withTs(url));
      toast("SIGNING LINK OPENED.");
    } catch {
      try {
        w.close();
      } catch {}
      setError("Unable to open the signing link right now. Please try again or contact support.");
    }
  };

  return (
    <div className="smc-page-container authz-scope">
      <div className="smc-page-content cortex-page-intro-shell">
        <CphereHeaderLogo
          alt="Powered by Logo"
          className="authz-header-logo"
          onClick={handleLogoClick}
        />

        <div className="smc-hero-container">
          <div className="smc-hero-pill">
            <span className="smc-hero-pill-label cphere-hero-pill-wordmark">
              Authorize
            </span>
            <span className="smc-hero-pill-tag cphere-hero-pill-tag">
              Media
            </span>
          </div>
          <div className="smc-hero-shimmer-bar" />
        </div>

        <div className="mediacampaigns-runs-card authz-desktop-panel">
          <div className="authz-header-stack">
            <div className="authz-title-pill">
              <div className="authz-title-pill-main">MEDIA AUTHORIZATIONS</div>
              <div className="authz-title-pill-sub">UPDATED: {now.toLocaleString()}</div>
            </div>

            <div className="authz-message-row">
              {!loading && error && (
                <div className="authz-error-message authz-inline-message">{error}</div>
              )}
              {!loading && notice && (
                <div className="authz-success-message authz-inline-message">{notice}</div>
              )}
            </div>
          </div>

          <div className="mediacampaigns-filters">
            <div className="mediacampaigns-filter-group mediacampaigns-upcoming-toggle">
              <label htmlFor="authz-pending-only">Pending Only</label>
              <input
                id="authz-pending-only"
                type="checkbox"
                checked={pendingOnly}
                onChange={(e) => setPendingOnly(e.target.checked)}
              />
            </div>
          </div>

          {!loading && filtered.length === 0 ? (
            <div className="authz-info-message authz-inline-message">No authorizations found.</div>
          ) : !loading ? (
            <div className="mediacampaigns-table-wrapper authz-responsive-table-wrap">
              <table className="mediacampaigns-table authz-table authz-responsive-table">
                <thead>
                  <tr>
                    <th scope="col">Advertiser</th>
                    <th scope="col">Campaign</th>
                    <th scope="col">Authorization</th>
                    <th scope="col">Urgency</th>
                    <th scope="col">Flight</th>
                    <th scope="col">Budget</th>
                    <th scope="col">D-Days</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((c) => {
                    const id = getCampaignId(c);

                    const governedStatus = getGovernedStatus(c);
                    const statusClass = `authz-status-badge authz-status-${getAuthorizationStatusVisualKey(
                      governedStatus
                    )}`;

                    const urgencyRaw = getUrgency(c, now);
                    const urgencyClass = `authz-urgency-badge authz-urgency-${urgencyToken(
                      urgencyRaw
                    )}`;

                    const { countdownDisplay, countdownTitle } = computeDDaysDisplay(c, now);
                    const dDaysLifecycleState = getDDaysLifecycleState(countdownDisplay);

                    const flightStartStr = formatFlightDate(getFlightStart(c));
                    const flightEndStr = formatFlightDate(getFlightEnd(c));
                    const contractDownloadAvailable = isContractDownloadAvailable(c);
                    const signingLinkAvailable = isSigningLinkAvailable(c);

                    return (
                      <tr
                        key={String(id ?? getCampaignName(c))}
                        className="mediacampaigns-row authz-responsive-row"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => openModal(c, e.currentTarget)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openModal(c, e.currentTarget);
                          }
                        }}
                      >
                        <td className="authz-responsive-cell" data-label="Advertiser">
                          <div className="authz-responsive-value authz-advertiser-name">
                            {getAdvertiserName(c)}
                          </div>
                        </td>

                        <td
                          className="mediacampaigns-cell-campaign authz-responsive-cell"
                          data-label="Campaign"
                        >
                          <div className="authz-responsive-value">
                            <div className="mediacampaigns-campaign-name">
                              {getCampaignName(c)}
                            </div>

                            {getEnvelopeId(c) && (
                              <div className="authz-subline">
                                Envelope: {String(getEnvelopeId(c)).slice(0, 10)}…
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="authz-responsive-cell" data-label="Authorization">
                          <div className="authz-responsive-value">
                            <span className={statusClass}>{prettyStatus(governedStatus)}</span>
                          </div>
                        </td>

                        <td className="authz-responsive-cell" data-label="Urgency">
                          <div className="authz-responsive-value">
                            <span className={urgencyClass}>{prettyUrgency(urgencyRaw)}</span>
                          </div>
                        </td>

                        <td className="authz-responsive-cell" data-label="Flight">
                          <div className="authz-responsive-value">
                            <div className="mediacampaigns-flight-dates">
                              <span>
                                {flightStartStr} → {flightEndStr}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="authz-responsive-cell" data-label="Budget">
                          <div className="authz-responsive-value">
                            {formatMoneySafe(getBudget(c))}
                          </div>
                        </td>

                        <td
                          className="mc-cell-ddays authz-responsive-cell"
                          data-label="D-Days"
                          title={countdownTitle}
                        >
                          <div className="authz-responsive-value">
                            {dDaysLifecycleState ? (
                              <span
                                className={`authz-ddays-pill authz-ddays-pill-${dDaysLifecycleState}`}
                              >
                                {countdownDisplay}
                              </span>
                            ) : (
                              countdownDisplay
                            )}
                          </div>
                        </td>

                        <td className="authz-responsive-cell" data-label="Actions">
                          <div className="authz-responsive-value">
                            <div className="authz-actions">
                              <button
                                className="authz-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openModal(c, e.currentTarget);
                                }}
                              >
                                View
                              </button>

                              {signingLinkAvailable ? (
                                <button
                                  className="authz-action-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenSigningLink(c);
                                  }}
                                >
                                  Open Signing Link
                                </button>
                              ) : (
                                <span className="authz-unavailable-pill">
                                  SIGNING LINK NOT AVAILABLE
                                </span>
                              )}

                              {contractDownloadAvailable ? (
                                <button
                                  className="authz-action-btn authz-icon-btn"
                                  title="View Contract"
                                  aria-label="View Contract"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(c);
                                  }}
                                >
                                  ⤓
                                </button>
                              ) : (
                                <span className="authz-unavailable-pill">
                                  CONTRACT NOT AVAILABLE
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        {/* Modal */}
        {open && active && (
          <div
            className="authz-modal-backdrop"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeModal();
            }}
            onTouchStart={(e) => {
              if (e.target === e.currentTarget) closeModal();
            }}
          >
            <div
              ref={modalRef}
              className="authz-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="authz-modal-title"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="authz-modal-top">
                <div className={`authz-modal-hero ${heroFailed ? "authz-modal-hero--fallback" : ""}`}>
                  {!heroFailed && (
                    <img
                      src={authorizeHero}
                      alt="Authorization"
                      className="authz-modal-hero-img"
                      loading="lazy"
                      decoding="async"
                      onError={() => setHeroFailed(true)}
                      draggable={false}
                    />
                  )}
                </div>

                <button
                  ref={closeBtnRef}
                  className="authz-modal-close"
                  onClick={closeModal}
                  aria-label="Close"
                  type="button"
                >
                  ✕
                </button>
              </div>

              <div className="authz-modal-body">
                <div className="authz-modal-title" id="authz-modal-title">
                  {getCampaignName(active)}
                </div>

                <div className="authz-modal-grid">
                  <div className="authz-modal-item">
                    <div className="authz-modal-label">Authorization</div>
                    <div className="authz-modal-value">
                      <span
                        className={`authz-status-badge authz-status-${getAuthorizationStatusVisualKey(
                          getGovernedStatus(active)
                        )}`}
                      >
                        {prettyStatus(getGovernedStatus(active))}
                      </span>
                    </div>
                  </div>

                  <div className="authz-modal-item">
                    <div className="authz-modal-label">Urgency</div>
                    <div className="authz-modal-value">
                      <span
                        className={`authz-urgency-badge authz-urgency-${urgencyToken(
                          getUrgency(active, now)
                        )}`}
                      >
                        {prettyUrgency(getUrgency(active, now))}
                      </span>
                    </div>
                  </div>

                  <div className="authz-modal-item">
                    <div className="authz-modal-label">Advertiser</div>
                    <div className="authz-modal-value">{getAdvertiserName(active)}</div>
                  </div>

                  <div className="authz-modal-item">
                    <div className="authz-modal-label">Flight</div>
                    <div className="authz-modal-value">
                      {formatFlightRange(getFlightStart(active), getFlightEnd(active))}
                    </div>
                  </div>

                  <div className="authz-modal-item">
                    <div className="authz-modal-label">Budget</div>
                    <div className="authz-modal-value">{formatMoneySafe(getBudget(active))}</div>
                  </div>

                  <div className="authz-modal-item">
                    <div className="authz-modal-label">Envelope</div>
                    <div className="authz-modal-value authz-mono">{getEnvelopeId(active) || "—"}</div>
                  </div>
                </div>

                <div className="authz-modal-actions">
                  {isContractDownloadAvailable(active) ? (
                    <button className="authz-action-btn" onClick={() => handleDownload(active)} type="button">
                      VIEW CONTRACT
                    </button>
                  ) : (
                    <span className="authz-unavailable-pill">
                      CONTRACT NOT AVAILABLE
                    </span>
                  )}

                  {isSigningLinkAvailable(active) ? (
                    <button className="authz-action-btn" onClick={() => handleOpenSigningLink(active)} type="button">
                      OPEN SIGNING LINK
                    </button>
                  ) : (
                    <span className="authz-unavailable-pill">
                      SIGNING LINK NOT AVAILABLE
                    </span>
                  )}
                </div>

                <div className="authz-modal-note">
                  Funding of authorized agreement is required to initiate service, connect AI technology, activate media buys,
                  enable cross platform optimization and allocate human resources. Many platforms charge in real-time and do not
                  extend credit.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SherlocAuthorizations;
